import { useState, useRef, useEffect } from "react"
import { Bot, Send, Copy, Check, AlertTriangle, RefreshCw, User } from "lucide-react"
import api from "../services/api"

const QUICK_PROMPTS = [
  "Explain the EMA crossover strategy and when it works best",
  "What is the Sharpe ratio and how do I interpret it?",
  "How do I calculate position size using the 1% rule?",
  "What are the key differences between scalping and swing trading?",
  "Explain max drawdown and why it matters",
  "What is backtesting overfitting and how do I avoid it?",
  "How does a grid trading bot work?",
  "What's the difference between RSI and MACD?",
  "Explain the Kelly Criterion for position sizing",
  "What is a market maker and how do they profit?",
]

const STRATEGIES = [
  { value: "ema_crossover", label: "EMA Crossover" },
  { value: "rsi_mean_reversion", label: "RSI Mean Reversion" },
  { value: "macd_momentum", label: "MACD Momentum" },
  { value: "bollinger_bands", label: "Bollinger Bands" },
  { value: "breakout", label: "Breakout" },
  { value: "grid_trading", label: "Grid Trading" },
  { value: "dca", label: "DCA Strategy" },
]

function renderMessage(text) {
  if (!text) return null
  const parts = text.split(/(`{3}[\s\S]*?`{3}|`[^`]+`|\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith("```") && part.endsWith("```")) {
      const code = part.slice(3, -3).replace(/^\w+\n/, "")
      return <pre key={i} className="bg-surface-200 rounded-lg p-3 text-xs font-mono overflow-x-auto my-2 text-gray-200">{code}</pre>
    }
    if (part.startsWith("`") && part.endsWith("`")) return <code key={i} className="bg-surface-200 px-1.5 py-0.5 rounded text-brand-300 text-xs font-mono">{part.slice(1,-1)}</code>
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} className="text-white font-semibold">{part.slice(2,-2)}</strong>
    return <span key={i}>{part}</span>
  })
}

function Message({ msg }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(msg.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  const isUser = msg.role === "user"
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${isUser ? "bg-brand-500/20" : "bg-surface-200"}`}>
        {isUser ? <User size={14} className="text-brand-400"/> : <Bot size={14} className="text-gray-300"/>}
      </div>
      <div className={`max-w-[80%] group relative ${isUser ? "text-right" : ""}`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${isUser ? "bg-brand-500/15 text-gray-100 rounded-tr-sm" : "bg-surface-100 text-gray-200 rounded-tl-sm"}`}>
          {msg.loading ? (
            <div className="flex gap-1 py-1">
              {[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: `${i*0.15}s` }}/>)}
            </div>
          ) : renderMessage(msg.content)}
        </div>
        {!isUser && !msg.loading && (
          <button onClick={copy} className="absolute -top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-surface-200 rounded-lg">
            {copied ? <Check size={11} className="text-trading-green"/> : <Copy size={11} className="text-gray-400"/>}
          </button>
        )}
      </div>
    </div>
  )
}

const STORAGE_KEY = "ai_chat_history"
const DEFAULT_MSG = { role: "assistant", content: "Hello! I'm your AI trading assistant. I can help you understand strategies, indicators, risk management concepts, and answer any questions about algorithmic trading. What would you like to know?" }

export default function AIAssistant() {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : [DEFAULT_MSG]
    } catch { return [DEFAULT_MSG] }
  })
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [strategyMode, setStrategyMode] = useState("")
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }) }, [messages])

  useEffect(() => {
    const toSave = messages.filter(m => !m.loading)
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave)) } catch {}
  }, [messages])

  async function send(text) {
    const content = text || input.trim()
    if (!content || loading) return
    setInput("")
    const userMsg = { role: "user", content }
    const loadingMsg = { role: "assistant", content: "", loading: true }
    setMessages(prev => [...prev, userMsg, loadingMsg])
    setLoading(true)
    try {
      const { data } = await api.post("/ai/chat", { message: content, strategy_context: strategyMode || undefined })
      setMessages(prev => [...prev.slice(0,-1), { role: "assistant", content: data.response || data.message || "I couldn't generate a response. Please try again." }])
    } catch (err) {
      const mock = getMockResponse(content)
      setMessages(prev => [...prev.slice(0,-1), { role: "assistant", content: mock }])
    } finally { setLoading(false) }
  }

  function getMockResponse(q) {
    const lower = q.toLowerCase()
    if (lower.includes("ema") || lower.includes("moving average")) return "**EMA (Exponential Moving Average)** gives more weight to recent price data compared to SMA. The EMA crossover strategy generates signals when a fast EMA (e.g. 9-period) crosses a slow EMA (e.g. 21-period).\n\n- **Golden Cross**: Fast EMA crosses above Slow EMA → Buy signal\n- **Death Cross**: Fast EMA crosses below Slow EMA → Sell signal\n\nBest used in trending markets. In ranging markets, it produces many false signals. Always confirm with volume or another indicator."
    if (lower.includes("sharpe")) return "**Sharpe Ratio** measures risk-adjusted return:\n\n`Sharpe = (Portfolio Return - Risk Free Rate) / Standard Deviation`\n\n**Interpretation:**\n- < 1.0: Suboptimal\n- 1.0 - 1.5: Good\n- 1.5 - 2.0: Very good\n- > 2.0: Excellent (but may indicate overfitting in backtests)\n\nA higher Sharpe means more return per unit of risk."
    if (lower.includes("rsi")) return "**RSI (Relative Strength Index)** measures momentum on a 0-100 scale.\n\n- **Overbought**: RSI > 70 → potential sell\n- **Oversold**: RSI < 30 → potential buy\n\n**RSI Mean Reversion Strategy**: Wait for RSI to reach extreme levels, then trade the expected reversal. Works best in ranging/sideways markets."
    if (lower.includes("drawdown")) return "**Max Drawdown** is the largest peak-to-trough decline in your equity curve.\n\n`Max DD = (Trough Value - Peak Value) / Peak Value × 100`\n\nIt represents the worst-case scenario an investor could experience. A 50% drawdown requires a **100% gain** just to break even — which is why limiting drawdown is crucial."
    return "Great question! To give you the most helpful answer, could you provide more context? I'm specialized in algorithmic trading topics including:\n\n- **Strategy design** (EMA, RSI, MACD, Bollinger Bands, etc.)\n- **Risk management** (position sizing, stop-losses, drawdown)\n- **Backtesting methodology**\n- **Technical indicators**\n- **Bot configuration and monitoring**\n\nFeel free to ask anything about these topics!"
  }

  function clearChat() {
    setMessages([DEFAULT_MSG])
    try { localStorage.removeItem(STORAGE_KEY) } catch {}
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] min-h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-brand-500/10 rounded-xl"><Bot size={20} className="text-brand-400"/></div>
          <div>
            <h2 className="font-bold text-white">AI Trading Assistant</h2>
            <p className="text-xs text-gray-400">Powered by advanced language model · Trading & strategy analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select className="input text-xs py-1.5" value={strategyMode} onChange={e => setStrategyMode(e.target.value)}>
            <option value="">Strategy context: None</option>
            {STRATEGIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={clearChat} className="btn-secondary flex items-center gap-1.5 text-xs py-1.5"><RefreshCw size={12}/> Clear</button>
        </div>
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Quick prompts */}
        <div className="hidden xl:flex flex-col gap-2 w-56 shrink-0">
          <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider">Quick Questions</p>
          <div className="space-y-1 overflow-y-auto">
            {QUICK_PROMPTS.map((p, i) => (
              <button key={i} onClick={() => send(p)} className="w-full text-left text-xs text-gray-300 hover:text-white bg-surface-100 hover:bg-surface-200 rounded-lg px-3 py-2 transition-colors line-clamp-2">{p}</button>
            ))}
          </div>
          <div className="mt-auto p-3 bg-surface-100 rounded-xl border border-trading-yellow/20">
            <div className="flex items-center gap-1.5 mb-1"><AlertTriangle size={11} className="text-trading-yellow"/><p className="text-[10px] font-semibold text-trading-yellow">Disclaimer</p></div>
            <p className="text-[10px] text-gray-500">For educational purposes only. Not financial advice. Always do your own research.</p>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          <div className="flex-1 overflow-y-auto space-y-4 pr-1 mb-4">
            {messages.map((msg, i) => <Message key={i} msg={msg}/>)}
            <div ref={messagesEndRef}/>
          </div>
          {/* Input */}
          <div className="flex gap-2">
            <input ref={inputRef} className="input flex-1 text-sm" placeholder="Ask about strategies, indicators, risk management..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} disabled={loading}/>
            <button onClick={() => send()} disabled={loading || !input.trim()} className="btn-primary px-4 disabled:opacity-40 flex items-center gap-2 shrink-0">
              <Send size={15}/><span className="hidden sm:inline text-sm">Send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
